import {
  LoaderCircle,
  Package,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Search,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import ProductoModal from '../components/bodega/ProductoModal'
import ModalMensaje from '../components/ui/ModalMensaje'
import { useEmpresa } from '../context/EmpresaContext'
import { useModalMensaje } from '../hooks/useModalMensaje'
import { supabase } from '../lib/supabase'
import { formatearCantidadBodega } from '../utils/bodega'

function Productos() {
  const { empresaActual } = useEmpresa()
  const { modal, abrirModal, actualizarModal } = useModalMensaje()

  const esAdministrador = empresaActual?.rol === 'administrador'

  const [productos, setProductos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [modalAbierto, setModalAbierto] = useState(false)
  const [productoEditar, setProductoEditar] = useState(null)
  const [actualizandoId, setActualizandoId] = useState(null)

  const montadoRef = useRef(true)

  useEffect(() => {
    montadoRef.current = true

    return () => {
      montadoRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!empresaActual?.id) {
      setProductos([])
      setCargando(false)
      return
    }

    cargarProductos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActual?.id])

  const cargarProductos = async () => {
    try {
      setCargando(true)
      setError('')

      const { data, error: errorConsulta } = await supabase
        .from('productos')
        .select(`
          id,
          empresa_id,
          codigo,
          nombre,
          categoria_id,
          unidad_medida_id,
          descripcion,
          stock_minimo,
          activo,
          creado_en,
          actualizado_en,
          categoria:categorias_producto!fk_producto_categoria_empresa(
            id,
            nombre
          ),
          unidad:unidades_medida!fk_producto_unidad_empresa(
            id,
            codigo,
            nombre
          )
        `)
        .eq('empresa_id', empresaActual.id)
        .order('codigo')

      if (errorConsulta) {
        throw errorConsulta
      }

      if (!montadoRef.current) return

      setProductos(data || [])
    } catch (errorCarga) {
      console.error('Error al cargar productos:', errorCarga)

      if (!montadoRef.current) return

      setError(
        errorCarga.message || 'No fue posible cargar los productos.'
      )
      setProductos([])
    } finally {
      if (montadoRef.current) {
        setCargando(false)
      }
    }
  }

  const productosFiltrados = productos.filter((producto) => {
    const termino = busqueda.trim().toLowerCase()

    if (!termino) return true

    const codigoCoincide = (producto.codigo || '')
      .toLowerCase()
      .includes(termino)

    const nombreCoincide = (producto.nombre || '')
      .toLowerCase()
      .includes(termino)

    const categoriaCoincide = (producto.categoria?.nombre || '')
      .toLowerCase()
      .includes(termino)

    return codigoCoincide || nombreCoincide || categoriaCoincide
  })

  const abrirCreacion = () => {
    setProductoEditar(null)
    setModalAbierto(true)
  }

  const abrirEdicion = (producto) => {
    setProductoEditar(producto)
    setModalAbierto(true)
  }

  const cerrarModalProducto = () => {
    setModalAbierto(false)
    setProductoEditar(null)
  }

  const alGuardar = (productoGuardado) => {
    const fueEdicion = Boolean(productoEditar)

    cerrarModalProducto()
    cargarProductos()

    abrirModal({
      tipo: 'success',
      titulo: fueEdicion ? 'Producto actualizado' : 'Producto creado',
      mensaje: `El producto "${productoGuardado?.nombre}" fue ${
        fueEdicion ? 'actualizado' : 'creado'
      } correctamente.`,
    })
  }

  const aplicarCambioEstado = async (
    producto,
    activar,
    esDesactivacion
  ) => {
    if (esDesactivacion) {
      actualizarModal({
        procesando: true,
        mostrarCancelar: false,
        textoAceptar: 'Desactivando...',
      })
    }

    try {
      setActualizandoId(producto.id)

      const { error: errorActualizar } = await supabase
        .from('productos')
        .update({ activo: activar })
        .eq('id', producto.id)
        .eq('empresa_id', empresaActual.id)

      if (errorActualizar) {
        throw errorActualizar
      }

      if (!montadoRef.current) return

      await cargarProductos()

      if (esDesactivacion) {
        abrirModal({
          tipo: 'success',
          titulo: 'Producto desactivado',
          mensaje: 'El producto fue desactivado correctamente.',
        })
      }
    } catch (errorEstado) {
      console.error(
        'Error al actualizar estado del producto:',
        errorEstado
      )

      if (!montadoRef.current) return

      abrirModal({
        tipo: 'error',
        titulo: 'No se pudo actualizar',
        mensaje:
          errorEstado.message ||
          'No fue posible actualizar el estado del producto.',
      })
    } finally {
      if (montadoRef.current) {
        setActualizandoId(null)
      }
    }
  }

  const cambiarEstado = (producto) => {
    const activar = !producto.activo

    if (!activar) {
      abrirModal({
        tipo: 'confirm',
        titulo: 'Desactivar producto',
        mensaje: `¿Deseas desactivar el producto "${producto.nombre}"?`,
        textoAceptar: 'Desactivar',
        textoCancelar: 'Cancelar',
        mostrarCancelar: true,
        onAceptar: () => aplicarCambioEstado(producto, false, true),
      })
      return
    }

    aplicarCambioEstado(producto, true, false)
  }

  const renderContenido = () => {
    if (cargando) {
      return (
        <div className="flex min-h-64 items-center justify-center">
          <div className="text-center">
            <LoaderCircle
              size={38}
              className="mx-auto animate-spin text-emerald-600"
            />

            <p className="mt-4 font-medium text-slate-500">
              Cargando productos...
            </p>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center shadow-sm">
          <p className="font-semibold text-red-700">{error}</p>

          <button
            type="button"
            onClick={cargarProductos}
            className="mt-4 rounded-lg bg-slate-800 px-5 py-2 font-semibold text-white transition hover:bg-slate-900"
          >
            Reintentar
          </button>
        </div>
      )
    }

    if (productos.length === 0) {
      return (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
            <Package
              size={28}
              className="text-emerald-600"
            />
          </div>

          <h3 className="mt-5 text-lg font-bold text-slate-900">
            No hay productos registrados
          </h3>

          <p className="mt-2 text-slate-500">
            {esAdministrador
              ? 'Presiona “Nuevo producto” para ingresar el primer registro.'
              : 'Aún no se han registrado productos.'}
          </p>

          {esAdministrador && (
            <button
              type="button"
              onClick={abrirCreacion}
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-600 px-5 py-3 font-semibold text-emerald-700 transition hover:bg-emerald-50"
            >
              <Plus size={19} />
              Ingresar primer producto
            </button>
          )}
        </div>
      )
    }

    return (
      <>
        <div className="relative mb-4 max-w-sm">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />

          <input
            type="text"
            value={busqueda}
            onChange={(event) => setBusqueda(event.target.value)}
            placeholder="Buscar por código, nombre o categoría"
            className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
          />
        </div>

        {productosFiltrados.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <p className="text-slate-500">
              No se encontraron productos para “{busqueda}”.
            </p>
          </div>
        ) : (
          <div className="w-full overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-sm md:min-w-[860px]">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-6 py-3">Código</th>
                  <th className="px-6 py-3">Nombre</th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Categoría
                  </th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Unidad
                  </th>
                  <th className="hidden px-6 py-3 lg:table-cell">
                    Stock mínimo
                  </th>
                  <th className="px-6 py-3">Estado</th>
                  {esAdministrador && (
                    <th className="px-6 py-3 text-right">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {productosFiltrados.map((producto) => (
                  <tr key={producto.id}>
                    <td className="px-6 py-4 font-semibold text-slate-900">
                      {producto.codigo}
                    </td>

                    <td className="px-6 py-4 text-slate-700">
                      {producto.nombre}
                    </td>

                    <td className="hidden px-6 py-4 text-slate-600 md:table-cell">
                      {producto.categoria?.nombre || 'Sin categoría'}
                    </td>

                    <td className="hidden px-6 py-4 text-slate-600 md:table-cell">
                      {producto.unidad?.codigo || '—'}
                    </td>

                    <td className="hidden px-6 py-4 text-slate-600 lg:table-cell">
                      {formatearCantidadBodega(
                        producto.stock_minimo
                      )}
                    </td>

                    <td className="px-6 py-4">
                      {producto.activo ? (
                        <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                          Activo
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                          Inactivo
                        </span>
                      )}
                    </td>

                    {esAdministrador && (
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => abrirEdicion(producto)}
                            title="Editar"
                            aria-label="Editar producto"
                            className="rounded-lg border border-slate-300 p-2 text-slate-600 transition hover:bg-slate-100"
                          >
                            <Pencil size={16} />
                          </button>

                          <button
                            type="button"
                            onClick={() => cambiarEstado(producto)}
                            disabled={
                              actualizandoId === producto.id
                            }
                            title={
                              producto.activo
                                ? 'Desactivar'
                                : 'Reactivar'
                            }
                            aria-label={
                              producto.activo
                                ? 'Desactivar producto'
                                : 'Reactivar producto'
                            }
                            className={
                              producto.activo
                                ? 'rounded-lg border border-red-200 p-2 text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60'
                                : 'rounded-lg border border-emerald-200 p-2 text-emerald-600 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60'
                            }
                          >
                            {actualizandoId === producto.id ? (
                              <LoaderCircle
                                size={16}
                                className="animate-spin"
                              />
                            ) : producto.activo ? (
                              <PowerOff size={16} />
                            ) : (
                              <Power size={16} />
                            )}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
            Mantenedores
          </p>

          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
            Productos
          </h2>

          <p className="mt-2 text-slate-500">
            Administra los productos de la empresa seleccionada.
          </p>
        </div>

        {esAdministrador && (
          <button
            type="button"
            onClick={abrirCreacion}
            className="flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700"
          >
            <Plus size={20} />
            Nuevo producto
          </button>
        )}
      </div>

      {renderContenido()}

      {modalAbierto && (
        <ProductoModal
          producto={productoEditar}
          onClose={cerrarModalProducto}
          onGuardado={alGuardar}
        />
      )}

      <ModalMensaje {...modal} />
    </div>
  )
}

export default Productos
